import Button from '@components/web/src/atoms/Buttons/Button';
import * as S from '@components/web/src/atoms/Tooltips/styles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/web/src/atoms/Tooltips/TooltipElement';
import { TooltipBodyProps, TooltipComponentProps } from '@components/web/src/atoms/Tooltips/types';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS, GRADIENTS } from '@components/web/src/styles';

export const TooltipBody = ({ variant = 'dark', title, description }: TooltipBodyProps) => {
  const titleTextColor = variant === 'light' ? COLORS.gray['700'] : COLORS.base.white;
  const descriptionTextColor = variant === 'light' ? COLORS.gray['500'] : COLORS.base.white;

  return (
    <S.Tooltip $isDescription={!!description} $variant={variant}>
      <Text color={titleTextColor} size="body2" text={title} weight="semibold" />
      {description && <Text color={descriptionTextColor} size="body2" text={description} />}
    </S.Tooltip>
  );
};

/**
 * Renders a Tooltip component with a trigger and content.
 *
 * @param {ReactNode} trigger - The trigger element that will display the tooltip when hovered or clicked.
 * By default Primary Button is used
 * @param {string} variant - The variant of the tooltip. Can be 'light', 'dark', or 'green'. Defaults to 'light'.
 * @param {ILocaleText} title - The title of the tooltip.
 * @param {ILocaleText} description - The description of the tooltip.
 * @param {boolean} isAlwaysOpen - Determines whether the tooltip should always be open. Defaults to false.
 * @param {Placement} placement - The placement of the tooltip. Defaults to 'top-start'.
 * @returns {ReactNode} - The rendered Tooltip component.
 */
const TooltipComponent = ({
  trigger,
  isVariantTasteId = false,
  variant = 'light',
  title,
  description,
  isAlwaysOpen = false,
  placement = 'top-start', // will auto-adjust on the available side
  triggerClassName,
  contentClassName,
  isShowOnHover = true,
}: TooltipComponentProps) => {
  const triggerComponent = trigger || <Button text="Tooltip Trigger" />;
  const isShowTooltip = !!title || !!description;

  const getColor = colorVariant => {
    switch (colorVariant) {
      case 'light':
        return COLORS.base.white;
      case 'green':
        return COLORS.green['600'];
      case 'dark':
      case 'gradient':
        return GRADIENTS.primaryGradient;
      default:
        return COLORS.gray['500'];
    }
  };

  return (
    <Tooltip
      isVariantTasteId={isVariantTasteId}
      placement={placement}
      {...(isAlwaysOpen ? { open: true } : {})}
      isShowOnHover={isShowOnHover}
    >
      <TooltipTrigger className={triggerClassName}>{triggerComponent}</TooltipTrigger>
      {isShowTooltip && (
        <TooltipContent className={contentClassName} color={getColor(variant)}>
          <TooltipBody description={description} title={title} variant={variant} />
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default TooltipComponent;
